import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import BrowsingCard from '@design-system/components/BrowsingCard/BrowsingCardV2';
import useRegionConfig from '@source/hooks/useRegionConfig';
import useWindowResize from '@source/hooks/useWindowResize';
import i18next from 'i18next';
import { TRegionKey } from '@source/interface';
import { getCarDetailsUrlFormat, carURLInfo } from '@source/utils/utils';
import { ButtonSlide } from '@source/types/common';
import { URL_CONFIG } from '@source/constants/urlConfig';
import type { Hit } from '@algolia/client-search';
import Slider, { LazyLoadTypes, Settings } from 'react-slick';
import styled, { css } from 'styled-components';
import { formatCarInfo } from '@design-system/components/BrowsingCard/utils';
import TitleHeader from './TitleHeader';

const MIN_CARD_WIDTH = 343;
const MAX_CARD_WIDTH = 385;

interface IBrowsingCardContianer {
  numberOfCars: number | undefined;
  isMYPromo?: boolean;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;

  .slick-track {
    display: flex !important;
    padding-bottom: 15px;
  }

  .slick-slide {
    height: inherit !important;
    flex: 1 1 337px;
    min-width: 0;
    max-width: 337px;

    @media only screen and (min-width: ${(props) => props.theme.metrics.breakpoints.xl}) {
      flex: unset;
      min-width: unset;
      max-width: unset;
    }

    .slick-active {
      min-width: ${MIN_CARD_WIDTH}px;
    }

    & div:not(.similarCars div) {
      height: 100%;
    }
  }

  .slick-arrow {
    display: none !important;
  }
`;

const StyledFullCarContainer = styled.div<{ numberOfCars: number | undefined }>`
  display: flex;
  flex-direction: column;
  width: fit-content;
  margin: auto;

  .cars {
    display: flex;
  }

  .similarCars {
    max-width: ${MAX_CARD_WIDTH}px;
  }

  ${(props) =>
    !!props.numberOfCars &&
    props.numberOfCars === 1 &&
    css`
      align-items: center;
    `}
`;

const BrowsingCardContainer = styled.div<IBrowsingCardContianer>`
  height: ${(props) => (props.isMYPromo ? '431px' : '100%')};
  display: ${(props) => (props.isMYPromo ? 'block' : 'flex')};
  max-width: ${MIN_CARD_WIDTH}px;
  margin: 0 4px;

  .lazy-load-image-background {
    width: 100% !important;

    img {
      object-fit: cover;
    }
  }

  @media only screen and (min-width: ${(props) => props.theme.metrics.breakpoints.sm}) {
    display: block;
    margin: 0 8px;

    ${(props) =>
      !!props.numberOfCars &&
      props.numberOfCars === 1 &&
      css`
        max-width: ${MAX_CARD_WIDTH}px;
      `}
  }

  @media only screen and (min-width: ${(props) => props.theme.metrics.breakpoints.md}) {
    max-width: ${MAX_CARD_WIDTH}px;
  }

  @media only screen and (min-width: ${(props) => props.theme.metrics.breakpoints.xl}) {
    max-width: 500px;
  }
`;

const BrowsingCardWrapperStyles = css`
  box-shadow: 0px 6px 6px rgba(161, 161, 161, 0.25);
  background: #ffffff;
  width: 100% !important;
  height: 100%;
`;

interface SimilarCarsProps {
  data: Hit<object>[];
  title: string | ReactNode;
  responsiveTitle: string | ReactNode;
  responsiveSubTitle: string | ReactNode;
  isMYPromo?: boolean;
  renderButtons?: (params: any) => JSX.Element;
  onOtherOnClickAction?: (params: any) => void;
}

const SimilarCars = ({
  data = [],
  title,
  responsiveTitle,
  responsiveSubTitle,
  isMYPromo,
  renderButtons,
  onOtherOnClickAction,
}: SimilarCarsProps) => {
  const {
    countryConfig: { country: countryCode },
  } = useRegionConfig();

  const containerRef = useRef<any>(null);

  const sliderRef = useRef<any>(null);

  const [width] = useWindowResize();

  const [pageIndex, setPageIndex] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(1);

  const [numberOfSliderItem, setNumberOfSliderItem] = useState(0);

  const isResponsiveSlider = width < 1280;

  const [isSafariOnIphone, setIsSafariOnIphone] = useState(false);

  useEffect(() => {
    const { userAgent } = navigator;
    const isIphone = /iPhone/i.test(userAgent);
    const isSafari = /Safari/i.test(userAgent) && !/Chrome/i.test(userAgent);

    if (isIphone && isSafari) {
      setIsSafariOnIphone(true);
    }
  }, []);

  useEffect(() => {
    if (isResponsiveSlider) return;

    if (containerRef.current) {
      let containerLeftPadding = 16;

      if (width >= 1280) containerLeftPadding = 100;
      else containerLeftPadding = 150;

      setNumberOfSliderItem(
        Math.round((containerRef.current.offsetWidth - containerLeftPadding * 2) / MIN_CARD_WIDTH) || 1,
      );
    }
  }, [width, containerRef, isResponsiveSlider]);

  const carsData = useMemo(() => {
    if (!data.length) return [];

    return data.map((car: any) => {
      const url = URL_CONFIG({
        region: countryCode as TRegionKey,
        lang: i18next.language,
        path: getCarDetailsUrlFormat(carURLInfo(car)),
      }).formatted;

      return (
        <div key={car?.listing?.slug} className="similarCars">
          <BrowsingCardContainer numberOfCars={data.length} isMYPromo={isMYPromo}>
            <BrowsingCard
              car={car}
              region={countryCode as TRegionKey}
              href={url}
              wrapperStyles={BrowsingCardWrapperStyles}
              hasLazyLoadImageEffect={false}
              photoSwiperOptions={{ allowTouchMove: false }}
              isExternal={isMYPromo}
              onOtherOnClickAction={onOtherOnClickAction}
              language={i18next.language || 'en'}
            />

            {renderButtons && renderButtons(formatCarInfo(car))}
          </BrowsingCardContainer>
        </div>
      );
    });
  }, [data, countryCode, isMYPromo, renderButtons, onOtherOnClickAction]);

  useEffect(() => {
    if (!numberOfSliderItem || !carsData.length) return;
    setPageSize(Math.ceil(carsData.length / numberOfSliderItem) || 1);
  }, [numberOfSliderItem, carsData]);

  const beforeChange = useCallback(
    (_: any, newIndex: number) => {
      const idx = newIndex + 1;
      if (newIndex === 0) setPageIndex(1);
      else setPageIndex(Math.ceil(idx / numberOfSliderItem));
    },
    [numberOfSliderItem],
  );

  const settings: Settings = useMemo(() => {
    const defaultSettings = {
      dots: false,
      infinite: true,
      speed: 500,
      useTransform: !isSafariOnIphone,
      beforeChange,
    };

    if (isResponsiveSlider) {
      return {
        className: 'slider variable-width',
        slidesToShow: 1,
        slidesToScroll: 1,
        variableWidth: true,
        centerMode: true,
        ...defaultSettings,
      };
    }

    const slidesToShow = numberOfSliderItem > data.length ? data.length : numberOfSliderItem;

    return {
      lazyLoad: 'ondemand' as LazyLoadTypes,
      slidesToShow,
      slidesToScroll: slidesToShow,
      ...defaultSettings,
    };
  }, [data, numberOfSliderItem, isResponsiveSlider, beforeChange]);

  const canShowAllCars = useMemo(() => {
    if (!carsData.length || !containerRef.current || !width) return false;

    if (carsData.length === 1) return true;

    let containerLeftPadding = 0;

    if (width >= 1920) containerLeftPadding = 320;
    else if (width >= 1280) containerLeftPadding = 52;
    else if (width >= 576) containerLeftPadding = 48;
    else containerLeftPadding = 16;

    const cars = Math.floor((containerRef.current.offsetWidth - containerLeftPadding * 2) / MAX_CARD_WIDTH);

    return carsData.length <= cars;
  }, [carsData, containerRef, width]);

  const slide = useCallback(
    (type: ButtonSlide) => {
      if (!sliderRef?.current) return;

      if (type === 'next') {
        sliderRef.current.slickNext();
      } else {
        sliderRef.current.slickPrev();
      }
    },
    [sliderRef],
  );

  return (
    <Container ref={containerRef}>
      {canShowAllCars ? (
        <StyledFullCarContainer numberOfCars={data.length}>
          <TitleHeader title={title} responsiveTitle={responsiveTitle} responsiveSubTitle={responsiveSubTitle} />
          <div className="cars">{carsData}</div>
        </StyledFullCarContainer>
      ) : (
        <>
          <TitleHeader
            pageIndex={pageIndex}
            pageSize={pageSize}
            slide={carsData.length > numberOfSliderItem ? slide : undefined}
            title={title}
            responsiveTitle={responsiveTitle}
            responsiveSubTitle={responsiveSubTitle}
          />

          {/* eslint-disable-next-line */}
          <Slider ref={sliderRef} {...settings}>
            {carsData}
          </Slider>
        </>
      )}
    </Container>
  );
};

SimilarCars.defaultProps = {
  isMYPromo: false,
  renderButtons: undefined,
  onOtherOnClickAction: undefined,
};

export default SimilarCars;
